/**
 * Which payment gateways are live right now — isomorphic, so client components
 * render the right CTA during SSR and after hydration.
 *
 * On the server it reads the per-gateway env flags directly (the branch is
 * compiled OUT of the client bundle by `import.meta.env.SSR`, same trick as
 * __root.tsx's DEPLOYMENT_ENV); on the client it reads back the id list the
 * server injected into `window.__BILLING_GATEWAYS__`.
 *
 * This replaces the single `STRIPE_ENABLED` boolean the UI used to branch on.
 * That flag conflated "Stripe works" with "purchasing works", so turning Stripe
 * off would have hidden every other rail's CTA too. Ask the question you
 * actually mean: `canSubscribe()` / `canBuyTopUps()` / `canManageBilling()`
 * combine enablement with the CAPABILITY table, so a rail that cannot host a
 * management portal never makes the app render a "Manage billing" button.
 *
 * Server-only code that needs the gateway ITSELF should go through
 * `registry.ts` (which applies the same enablement); this module is only about
 * what to show.
 */

import {
  GATEWAY_CAPABILITIES,
  GATEWAY_DEFAULT_ENABLED,
  GATEWAY_ENV_FLAG,
  GATEWAY_IDS,
  type GatewayCapabilities,
  type GatewayId,
} from '@/lib/billing/gateways/types'

declare global {
  interface Window {
    __BILLING_GATEWAYS__?: GatewayId[]
  }
}

/**
 * The enabled ids as computed on the SERVER. Exported for `__root.tsx`, which
 * injects the result for the client, and for server code that wants the list
 * without the isomorphic indirection. Reads `process.env` directly on purpose:
 * this must work before/outside the validated `env` object (it runs during SSR
 * render), exactly as the old kill-switch did.
 */
export function serverEnabledGatewayIds(): GatewayId[] {
  // An unset flag falls back to the gateway's declared default, so a
  // default-off rail cannot enable itself in a deploy whose env predates it.
  return GATEWAY_IDS.filter((id) => {
    const raw = process.env[GATEWAY_ENV_FLAG[id]]
    if (raw === undefined || raw === '') return GATEWAY_DEFAULT_ENABLED[id]
    return raw !== 'false'
  })
}

/**
 * Enabled gateway ids, isomorphic. On the client, a missing global means the
 * injection script did not run — fall back to each gateway's DECLARED default
 * so a CTA is offered rather than silently hidden. The server functions re-check
 * enablement anyway, so a wrong optimistic guess costs an error message, never a
 * bad charge.
 *
 * The fallback is per-gateway rather than blanket "all enabled": a default-OFF
 * rail must stay off when the injection is missing, or a deploy that has never
 * heard of it renders its CTA to every customer.
 */
export function enabledGatewayIds(): GatewayId[] {
  if (import.meta.env.SSR) return serverEnabledGatewayIds()
  // Missing global = the injection script did not run. Offer only the
  // default-on gateways: a blanket fallback would render CTAs for rails that
  // are deliberately off. The server re-checks, so a wrong optimistic guess
  // costs an error message, never a bad charge.
  return (
    window.__BILLING_GATEWAYS__ ??
    GATEWAY_IDS.filter((id) => GATEWAY_DEFAULT_ENABLED[id])
  )
}

/** Whether a SPECIFIC rail is live. Used where the choice is the customer's —
 *  the payment-method step names its options, so it cannot ask by capability. */
export function isGatewayEnabled(id: GatewayId): boolean {
  return enabledGatewayIds().includes(id)
}

/** Enabled gateways that have a given capability. */
export function gatewaysWithCapability(
  capability: keyof GatewayCapabilities,
): GatewayId[] {
  return enabledGatewayIds().filter(
    (id) => GATEWAY_CAPABILITIES[id][capability],
  )
}

/** Any live rail can sell a subscription. */
export function canSubscribe(): boolean {
  return gatewaysWithCapability('subscriptions').length > 0
}

/** Any live rail can sell a data top-up. */
export function canBuyTopUps(): boolean {
  return gatewaysWithCapability('topUps').length > 0
}

/** Any live rail hosts a customer-facing management portal. Whether a given
 *  ORG can reach one also depends on how it pays — see `OrgPlanStatus.canManageBilling`. */
export function canManageBilling(): boolean {
  return gatewaysWithCapability('hostedManagement').length > 0
}
